// /admin/settings — Org & branding editor (T118, expanded T269).
//
// Sections:
//   1. Org identity: name + logo URL (shown in the nav wordmark area).
//   2. Defaults: default branch policy, default quality profile,
//      default new-code period (T254/T269), data retention (T269).
//
// All fields are submitted together in one PATCH. The quality-profile
// selector is populated from GET /api/v1/admin/quality-profiles (read-only
// here; full CRUD is T119).
//
// Requires org_admin — the backend RequireRole middleware enforces it.

import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR, { mutate as globalMutate } from 'swr';
import {
  Bot,
  Building2,
  Check,
  Clock,
  Database,
  ExternalLink,
  KeyRound,
  Layers,
  Loader2,
  Settings,
  ToggleLeft,
  ToggleRight,
  Users,
} from 'lucide-react';
import {
  ADMIN_LLM_SETTINGS_KEY,
  ADMIN_SIBLING_TOOLS_KEY,
  ApiError,
  LLMSettingsDTO,
  OrgSettings,
  OrgSettingsPatch,
  QualityProfileItem,
  SiblingToolConfig,
  adminSettingsApi,
  apiRequest,
  fetcher,
  llmSettingsApi,
  siblingToolsApi,
} from '../lib/api';
import { useLicenseState } from '../lib/useLicenseState';
import { PageHeader } from '../components/ui';

const SETTINGS_KEY = '/api/v1/admin/settings';
const PROFILES_KEY = '/api/v1/admin/quality-profiles';

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function AdminSettings() {
  const { data: settings, error: settingsErr, isLoading: settingsLoading, mutate } =
    useSWR<OrgSettings>(SETTINGS_KEY, fetcher);

  const { data: profiles, isLoading: profilesLoading } =
    useSWR<QualityProfileItem[]>(PROFILES_KEY, fetcher, { revalidateOnFocus: false });

  return (
    <div className="max-w-3xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Settings />}
        title="Settings"
        description="Org identity, branding, and global defaults. Changes apply immediately."
        actions={
          <>
            <Link
              to="/admin/quality-profiles"
              className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
            >
              <Layers className="h-3.5 w-3.5" aria-hidden="true" />
              Quality Profiles
            </Link>
            <Link
              to="/admin/users"
              className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
            >
              <Users className="h-3.5 w-3.5" aria-hidden="true" />
              User Management
            </Link>
          </>
        }
      />

      {(() => {
        if (settingsLoading) {
          return (
            <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              <span className="text-sm">Loading settings…</span>
            </div>
          );
        } else if (settingsErr) {
          return <ErrorBanner error={settingsErr} />;
        } else if (settings) {
          return (
            <SettingsForm
              settings={settings}
              profiles={profiles ?? []}
              profilesLoading={profilesLoading}
              onSaved={(updated) => mutate(updated, false)}
            />
          );
        } else {
          return null;
        }
      })()}

      <LLMSection />
      <SiblingToolsSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

interface SettingsFormProps {
  readonly settings: OrgSettings;
  readonly profiles: QualityProfileItem[];
  readonly profilesLoading: boolean;
  readonly onSaved: (updated: OrgSettings) => void;
}

function SettingsForm({ settings, profiles, profilesLoading, onSaved }: SettingsFormProps) {
  const { isExpired } = useLicenseState();
  const [name, setName] = useState(settings.name);
  const [logoURL, setLogoURL] = useState(settings.logo_url);
  const [branchPolicy, setBranchPolicy] = useState(settings.default_branch_policy || 'main');
  const [qualityProfileID, setQualityProfileID] = useState(settings.default_quality_profile_id);
  const [periodType, setPeriodType] = useState(settings.default_new_code_period_type ?? '');
  const [periodValue, setPeriodValue] = useState(settings.default_new_code_period_value ?? '');
  const [retentionDays, setRetentionDays] = useState(
    settings.data_retention_days > 0 ? String(settings.data_retention_days) : '',
  );

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Keep local state in sync if the SWR key revalidates from above.
  const prevID = useRef(settings.id);
  useEffect(() => {
    if (settings.id !== prevID.current) {
      prevID.current = settings.id;
      setName(settings.name);
      setLogoURL(settings.logo_url);
      setBranchPolicy(settings.default_branch_policy || 'main');
      setQualityProfileID(settings.default_quality_profile_id);
      setPeriodType(settings.default_new_code_period_type ?? '');
      setPeriodValue(settings.default_new_code_period_value ?? '');
      setRetentionDays(
        settings.data_retention_days > 0 ? String(settings.data_retention_days) : '',
      );
    }
  }, [settings]);

  // Normalise retention input to a number for dirty comparison so that
  // typing "0" when the server value is already 0 is not considered dirty.
  const retentionNum = retentionDays === '' ? 0 : (parseInt(retentionDays, 10) || 0);

  const isDirty =
    name !== settings.name ||
    logoURL !== settings.logo_url ||
    branchPolicy !== (settings.default_branch_policy || 'main') ||
    qualityProfileID !== settings.default_quality_profile_id ||
    periodType !== (settings.default_new_code_period_type ?? '') ||
    periodValue !== (settings.default_new_code_period_value ?? '') ||
    retentionNum !== settings.data_retention_days;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isDirty || saving) return;
    if (!name.trim()) {
      setErr('Org name must not be empty.');
      return;
    }
    setSaving(true);
    setErr(null);
    setSaved(false);

    const patch: OrgSettingsPatch = {};
    if (name !== settings.name) patch.name = name.trim();
    if (logoURL !== settings.logo_url) patch.logo_url = logoURL;
    if (branchPolicy !== (settings.default_branch_policy || 'main')) {
      patch.default_branch_policy = branchPolicy;
    }
    if (qualityProfileID !== settings.default_quality_profile_id) {
      patch.default_quality_profile_id = qualityProfileID;
    }
    if (periodType !== (settings.default_new_code_period_type ?? '')) {
      patch.default_new_code_period_type = periodType;
    }
    if (periodValue !== (settings.default_new_code_period_value ?? '')) {
      patch.default_new_code_period_value = periodValue;
    }
    if (retentionNum !== settings.data_retention_days) {
      patch.data_retention_days = retentionNum;
    }

    try {
      const updated = await adminSettingsApi.update(patch);
      onSaved(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      let msg: string;
      if (e instanceof ApiError) {
        msg = `${e.code}: ${e.message}`;
      } else if (e instanceof Error) {
        msg = e.message;
      } else {
        msg = 'request failed';
      }
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" aria-label="Org settings">

      {/* Identity section */}
      <section className="orb-card p-6 space-y-5">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="h-4 w-4 text-accent" aria-hidden="true" />
          <h2 className="orb-section-title text-sm">Identity</h2>
        </div>

        <Field label="Org name" htmlFor="org-name" required>
          <input
            id="org-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
            placeholder="Acme Corp"
            required
          />
        </Field>

        <Field
          label="Logo URL"
          htmlFor="org-logo"
          hint="Full URL to a square image (PNG/SVG, min 64×64). Shown in the nav bar."
        >
          <input
            id="org-logo"
            type="url"
            value={logoURL}
            onChange={(e) => setLogoURL(e.target.value)}
            className={inputCls}
            placeholder="https://example.com/logo.png"
          />
          {logoURL ? (
            <div className="mt-2 flex items-center gap-3">
              <img
                src={logoURL}
                alt="Logo preview"
                className="h-10 w-10 rounded object-contain bg-bg-inset border border-border"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <span className="text-xs text-fg-subtle">Preview</span>
            </div>
          ) : null}
        </Field>
      </section>

      {/* Defaults section */}
      <section className="orb-card p-6 space-y-5">
        <h2 className="orb-section-title text-sm mb-1">Defaults</h2>

        <Field
          label="Default branch policy"
          htmlFor="org-branch"
          hint="Branch name used when creating new projects if not specified."
        >
          <input
            id="org-branch"
            type="text"
            value={branchPolicy}
            onChange={(e) => setBranchPolicy(e.target.value)}
            className={inputCls}
            placeholder="main"
          />
        </Field>

        <Field
          label="Default quality profile"
          htmlFor="org-qp"
          hint="Profile applied to projects that have no explicit profile assigned."
        >
          {profilesLoading ? (
            <div className="flex items-center gap-2 text-fg-muted text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Loading profiles…
            </div>
          ) : (
            <select
              id="org-qp"
              value={qualityProfileID}
              onChange={(e) => setQualityProfileID(e.target.value)}
              className={`${inputCls} pr-8`}
              aria-label="Default quality profile"
            >
              <option value="">— none —</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.language}){p.is_default ? ' ★' : ''}
                </option>
              ))}
            </select>
          )}
        </Field>
      </section>

      {/* New-code period section */}
      <section className="orb-card p-6 space-y-5">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="h-4 w-4 text-accent" aria-hidden="true" />
          <h2 className="orb-section-title text-sm">New-Code Period</h2>
        </div>
        <p className="text-[11px] text-fg-subtle -mt-3">
          Defines what counts as "new code" for quality-gate evaluation. Projects
          can override this individually. Mirrors the Clean as You Code model.
        </p>

        <Field
          label="Period type"
          htmlFor="org-period-type"
          hint='How to determine the new-code boundary for this org.'
        >
          <select
            id="org-period-type"
            value={periodType}
            onChange={(e) => {
              setPeriodType(e.target.value);
              if (!['days', 'reference_branch'].includes(e.target.value)) {
                setPeriodValue('');
              }
            }}
            className={`${inputCls} pr-8`}
          >
            <option value="">PR diff only (default)</option>
            <option value="previous_version">Since previous version</option>
            <option value="days">Last N days</option>
            <option value="reference_branch">Reference branch</option>
          </select>
        </Field>

        {periodType === 'days' && (
          <Field
            label="Number of days"
            htmlFor="org-period-days"
            hint="Issues first detected in the last N days are considered new."
          >
            <input
              id="org-period-days"
              type="number"
              min={1}
              value={periodValue}
              onChange={(e) => setPeriodValue(e.target.value)}
              className={inputCls}
              placeholder="30"
            />
          </Field>
        )}

        {periodType === 'reference_branch' && (
          <Field
            label="Reference branch"
            htmlFor="org-period-branch"
            hint="Issues absent on this branch are considered new."
          >
            <input
              id="org-period-branch"
              type="text"
              value={periodValue}
              onChange={(e) => setPeriodValue(e.target.value)}
              className={inputCls}
              placeholder="main"
            />
          </Field>
        )}
      </section>

      {/* Data retention section */}
      <section className="orb-card p-6 space-y-5">
        <div className="flex items-center gap-2 mb-1">
          <Database className="h-4 w-4 text-accent" aria-hidden="true" />
          <h2 className="orb-section-title text-sm">Data Retention</h2>
        </div>

        <Field
          label="Retention period (days)"
          htmlFor="org-retention"
          hint="Scan and issue history older than this is eligible for pruning. Leave blank or set to 0 to keep data forever."
        >
          <input
            id="org-retention"
            type="number"
            min={0}
            value={retentionDays}
            onChange={(e) => setRetentionDays(e.target.value)}
            className={inputCls}
            placeholder="0 — keep forever"
          />
        </Field>
      </section>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div>
          {err ? (
            <p role="alert" className="text-xs text-danger">{err}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {saved ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-success">
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              Saved
            </span>
          ) : null}
          <button
            type="submit"
            disabled={!isDirty || saving || isExpired}
            title={isExpired ? 'License required — upload a license to save settings' : undefined}
            className="
              inline-flex items-center gap-1.5 px-4 py-2 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              bg-accent text-accent-fg hover:opacity-90
              border border-accent/40 transition-opacity
              disabled:opacity-30 disabled:cursor-not-allowed
            "
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

    </form>
  );
}

// ---------------------------------------------------------------------------
// Field wrapper
// ---------------------------------------------------------------------------

interface FieldProps {
  readonly label: string;
  readonly htmlFor: string;
  readonly hint?: string;
  readonly required?: boolean;
  readonly children: React.ReactNode;
}

function Field({ label, htmlFor, hint, required, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="orb-section-title text-[11px]">
        {label}
        {required ? <span className="text-danger ml-0.5">*</span> : null}
      </label>
      {children}
      {hint ? <p className="text-[11px] text-fg-subtle">{hint}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const inputCls = `
  w-full px-3 py-2 rounded-md border border-border
  bg-bg-inset text-fg text-sm placeholder-fg-subtle
  focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
  transition-colors
`.trim();

// ---------------------------------------------------------------------------
// LLM / AI-remediation section (T340)
// ---------------------------------------------------------------------------

const FEATURES_KEY = '/api/v1/admin/features';
const AI_REMEDIATION_FLAG_KEY = 'ai_remediation';

interface FeatureFlagDTO {
  key: string;
  enabled: boolean;
}

interface FeaturesResponse {
  flags: FeatureFlagDTO[];
}

function LLMSection() {
  const { isExpired } = useLicenseState();
  const { data: settings, isLoading: settingsLoading, error: settingsErr } =
    useSWR<LLMSettingsDTO>(ADMIN_LLM_SETTINGS_KEY, fetcher, { revalidateOnFocus: false });
  const { data: features, isLoading: featuresLoading } =
    useSWR<FeaturesResponse>(FEATURES_KEY, fetcher, { revalidateOnFocus: false });

  const aiFlag = features?.flags.find((f) => f.key === AI_REMEDIATION_FLAG_KEY);
  const flagEnabled = aiFlag?.enabled ?? false;

  const [enabled, setEnabled] = useState(false);
  const [model, setModel] = useState('');
  const [dailyBudget, setDailyBudget] = useState('');
  const [rpm, setRpm] = useState('');
  const [apiKey, setApiKey] = useState(''); // write-only; never pre-filled from server

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Sync local form state from server data (on load and after mutations).
  useEffect(() => {
    setEnabled(flagEnabled);
  }, [flagEnabled]);

  useEffect(() => {
    if (!settings) return;
    setModel(settings.model);
    setDailyBudget(String(settings.daily_token_budget));
    setRpm(String(settings.requests_per_minute));
  }, [settings]);

  const isDirty =
    enabled !== flagEnabled ||
    apiKey !== '' ||
    (settings != null &&
      (model !== settings.model ||
        dailyBudget !== String(settings.daily_token_budget) ||
        rpm !== String(settings.requests_per_minute)));

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    setErr(null);
    setSaved(false);
    try {
      const patch: { provider?: string; api_key?: string; model?: string; daily_token_budget?: number; requests_per_minute?: number } = {};
      if (settings && model !== settings.model) patch.model = model;
      if (settings && dailyBudget !== String(settings.daily_token_budget)) {
        patch.daily_token_budget = parseInt(dailyBudget, 10) || 0;
      }
      if (settings && rpm !== String(settings.requests_per_minute)) {
        patch.requests_per_minute = parseInt(rpm, 10) || 0;
      }
      if (apiKey !== '') patch.api_key = apiKey;

      if (Object.keys(patch).length > 0) {
        await llmSettingsApi.update(patch);
      }
      if (enabled !== flagEnabled) {
        await apiRequest<FeaturesResponse>(FEATURES_KEY, {
          method: 'PATCH',
          body: JSON.stringify({ key: AI_REMEDIATION_FLAG_KEY, enabled }),
        });
      }
      setApiKey('');
      await Promise.all([globalMutate(ADMIN_LLM_SETTINGS_KEY), globalMutate(FEATURES_KEY)]);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      let errMsg: string;
      if (e instanceof ApiError) {
        errMsg = `${e.code}: ${e.message}`;
      } else if (e instanceof Error) {
        errMsg = e.message;
      } else {
        errMsg = 'request failed';
      }
      setErr(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const loading = settingsLoading || featuresLoading;

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className="flex items-center gap-2 text-fg-muted text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Loading…
      </div>
    );
  } else if (settingsErr) {
    body = <p className="text-xs text-danger">Failed to load LLM settings.</p>;
  } else {
    body = (
      <div className="space-y-5">
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label="Enable AI Remediation"
              onClick={() => setEnabled((v) => !v)}
              disabled={isExpired}
              className={`
                relative inline-flex h-5 w-9 shrink-0 items-center rounded-full
                transition-colors focus:outline-none focus:ring-2 focus:ring-accent/40
                disabled:opacity-30 disabled:cursor-not-allowed
                ${enabled ? 'bg-accent' : 'bg-fg-subtle/30'}
              `}
            >
              <span
                className={`
                  inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow
                  transition-transform
                  ${enabled ? 'translate-x-[18px]' : 'translate-x-[2px]'}
                `}
              />
            </button>
            <span className="text-sm font-medium text-fg inline-flex items-center gap-1.5">
              {enabled ? (
                <ToggleRight className="h-4 w-4 text-accent" aria-hidden="true" />
              ) : (
                <ToggleLeft className="h-4 w-4 text-fg-muted" aria-hidden="true" />
              )}
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          <Field label="Provider" htmlFor="llm-provider" hint="Only Anthropic is supported today.">
            <input
              id="llm-provider"
              type="text"
              value="anthropic"
              disabled
              className={`${inputCls} opacity-60`}
            />
          </Field>

          <Field
            label="Model"
            htmlFor="llm-model"
            hint="Anthropic model ID. Leave blank to use the server default."
          >
            <input
              id="llm-model"
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className={inputCls}
              placeholder="claude-sonnet-4-6"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Daily token budget"
              htmlFor="llm-budget"
              hint="Max suggest-fix calls per org per day. 0 = unlimited."
            >
              <input
                id="llm-budget"
                type="number"
                min={0}
                value={dailyBudget}
                onChange={(e) => setDailyBudget(e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field
              label="Requests per minute"
              htmlFor="llm-rpm"
              hint="Max suggest-fix calls per user per minute. 0 = unlimited."
            >
              <input
                id="llm-rpm"
                type="number"
                min={0}
                value={rpm}
                onChange={(e) => setRpm(e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>

          <Field
            label="API key"
            htmlFor="llm-api-key"
            hint={
              settings?.has_api_key
                ? 'A key is configured. Leave blank to keep it, or enter a new one to replace it.'
                : 'No key configured yet — falls back to the server-wide ORBITALLINT_LLM_API_KEY.'
            }
          >
            <div className="flex items-center gap-2">
              <KeyRound className="h-3.5 w-3.5 text-fg-subtle shrink-0" aria-hidden="true" />
              <input
                id="llm-api-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className={inputCls}
                placeholder={settings?.has_api_key ? '•••••••••••••• (configured)' : 'sk-ant-…'}
                autoComplete="new-password"
              />
            </div>
          </Field>
        </div>
    );
  }

  return (
    <section className="orb-card p-6 space-y-5 mt-6">
      <div className="flex items-center gap-2 mb-1">
        <Bot className="h-4 w-4 text-accent" aria-hidden="true" />
        <h2 className="orb-section-title text-sm">AI Remediation (LLM)</h2>
      </div>
      <p className="text-[11px] text-fg-subtle -mt-3">
        Configure the Anthropic Claude integration behind the Issue Detail drawer's
        "Suggest fix" button. Enabling this also flips the org's{' '}
        <code className="font-mono bg-bg-inset px-1 py-0.5 rounded text-accent text-[10px]">
          ai_remediation
        </code>{' '}
        feature flag.
      </p>

      {body}

      <div className="flex items-center justify-between pt-1">
        <div>
          {err ? <p role="alert" className="text-xs text-danger">{err}</p> : null}
        </div>
        <div className="flex items-center gap-3">
          {saved ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-success">
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              Saved
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving || isExpired || loading}
            title={isExpired ? 'License required — upload a license to save settings' : undefined}
            className="
              inline-flex items-center gap-1.5 px-4 py-2 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              bg-accent text-accent-fg hover:opacity-90
              border border-accent/40 transition-opacity
              disabled:opacity-30 disabled:cursor-not-allowed
            "
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sibling-tool switcher section (T295)
// ---------------------------------------------------------------------------

const TOOL_LABELS: Record<string, string> = {
  git: 'Orbital/Git',
  reg: 'Orbital/Reg',
};

interface ToolRowState {
  enabled: boolean;
  baseURL: string;
}

function SiblingToolsSection() {
  const { isExpired } = useLicenseState();
  const { data, isLoading, mutate } = useSWR<SiblingToolConfig[]>(
    ADMIN_SIBLING_TOOLS_KEY,
    fetcher,
    { revalidateOnFocus: false },
  );

  const [rows, setRows] = useState<Record<string, ToolRowState>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Sync local state from server data (on load and after mutations).
  useEffect(() => {
    if (!data) return;
    const next: Record<string, ToolRowState> = {};
    for (const t of data) {
      next[t.key] = { enabled: t.enabled, baseURL: t.base_url };
    }
    setRows(next);
  }, [data]);

  const isDirty = (data ?? []).some((t) => {
    const r = rows[t.key];
    return r && (r.enabled !== t.enabled || r.baseURL !== t.base_url);
  });

  const validateRows = (): string | null => {
    for (const key of Object.keys(rows)) {
      const r = rows[key];
      if (r.enabled && !r.baseURL.startsWith('https://')) {
        return `${TOOL_LABELS[key] ?? key}: URL must start with https://`;
      }
    }
    return null;
  };

  const patchChangedTools = async () => {
    for (const t of (data ?? [])) {
      const r = rows[t.key];
      if (!r) continue;
      if (r.enabled !== t.enabled || r.baseURL !== t.base_url) {
        await siblingToolsApi.patch(t.key, r.enabled, r.baseURL);
      }
    }
  };

  const handleSave = async () => {
    if (!isDirty || saving) return;
    const validationErr = validateRows();
    if (validationErr) { setErr(validationErr); return; }
    setSaving(true);
    setErr(null);
    setSaved(false);
    try {
      await patchChangedTools();
      await mutate();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      let errMsg: string;
      if (e instanceof ApiError) {
        errMsg = `${e.code}: ${e.message}`;
      } else if (e instanceof Error) {
        errMsg = e.message;
      } else {
        errMsg = 'request failed';
      }
      setErr(errMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="orb-card p-6 space-y-5 mt-6">
      <div className="flex items-center gap-2 mb-1">
        <ExternalLink className="h-4 w-4 text-accent" aria-hidden="true" />
        <h2 className="orb-section-title text-sm">Tool Switcher</h2>
      </div>
      <p className="text-[11px] text-fg-subtle -mt-3">
        Link to sibling Orbital tools. Enable a tool and provide its base URL to
        show it in the wordmark switcher for all users in this org. Requires a
        shared-IdP SAML setup for SSO across tools.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-fg-muted text-xs">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          Loading…
        </div>
      ) : (
        <div className="space-y-5">
          {['git', 'reg'].map((key) => {
            const r = rows[key] ?? { enabled: false, baseURL: '' };
            const label = TOOL_LABELS[key] ?? `Orbital/${key}`;
            return (
              <div key={key} className="space-y-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={r.enabled}
                    aria-label={`Enable ${label}`}
                    onClick={() =>
                      setRows((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], enabled: !r.enabled },
                      }))
                    }
                    disabled={isExpired}
                    className={`
                      relative inline-flex h-5 w-9 shrink-0 items-center rounded-full
                      transition-colors focus:outline-none focus:ring-2 focus:ring-accent/40
                      disabled:opacity-30 disabled:cursor-not-allowed
                      ${r.enabled ? 'bg-accent' : 'bg-fg-subtle/30'}
                    `}
                  >
                    <span
                      className={`
                        inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow
                        transition-transform
                        ${r.enabled ? 'translate-x-[18px]' : 'translate-x-[2px]'}
                      `}
                    />
                  </button>
                  <span className="text-sm font-medium text-fg">{label}</span>
                </div>

                {r.enabled && (
                  <Field
                    label="Base URL"
                    htmlFor={`sibling-url-${key}`}
                    hint={`Full HTTPS URL for ${label}. Users are navigated here when switching tools.`}
                  >
                    <input
                      id={`sibling-url-${key}`}
                      type="url"
                      value={r.baseURL}
                      onChange={(e) =>
                        setRows((prev) => ({
                          ...prev,
                          [key]: { ...prev[key], baseURL: e.target.value },
                        }))
                      }
                      disabled={isExpired}
                      className={inputCls}
                      placeholder="https://git.example.com"
                    />
                  </Field>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <div>
          {err ? <p role="alert" className="text-xs text-danger">{err}</p> : null}
        </div>
        <div className="flex items-center gap-3">
          {saved ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-success">
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              Saved
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving || isExpired}
            title={isExpired ? 'License required — upload a license to save settings' : undefined}
            className="
              inline-flex items-center gap-1.5 px-4 py-2 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              bg-accent text-accent-fg hover:opacity-90
              border border-accent/40 transition-opacity
              disabled:opacity-30 disabled:cursor-not-allowed
            "
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Error banner
// ---------------------------------------------------------------------------

function ErrorBanner({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can manage settings.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}
